import React, { useCallback, useEffect, useMemo } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { AppActivityTableRow } from './appActivityTableRow';
import { ColorPalette } from '../../styles/constants';
import { Activity } from '../../models/activity';
import { generateIntervalDates } from '../../services/dateService';
import { useGetDaysOffQuery } from '../../store/daysOff/daysOffService';
import { addDays, format, isEqual, parse, parseISO } from 'date-fns';
import { COUNTRIES } from '../../constants';
import {
   formatDateOnly,
} from '../../services/dateService';


const styles = {
  columnName: {
    fontWeight: 600,
    borderRight: '1px solid white',
  },
  tableHead: {
    background: ColorPalette.LESS_LIGHT_GRAY,
  },
  smallColumn: {
    width: '10%',
  },
  mediumColumn: {
    width: '20%',
  },
};

interface ReportsViewsProps {
  startDate: string;
  endDate: string;
  location?: string;
  activities: Activity[];
  employeeWorkHours?: number;
  onEditActivity?: (activity: Activity) => void;
  onDeleteActivity?: (activity: Activity) => void;
  onAddActivity?: (addDate: string) => void;
  isEmployeeSection?: boolean;
}

export interface DateTableObject {
  [date: string]: Activity[];
}

export function AppActivityTableViews({
  startDate,
  endDate,
  location,
  activities,
  employeeWorkHours,
  onEditActivity,
  onDeleteActivity,
  onAddActivity,
  isEmployeeSection,
}: ReportsViewsProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const hasAMSActivities = useMemo(() => {
    return activities.some((activity) => {
      if (activity.task?.isAMS) {
        return true;
      }
      
      if (activity.task?.project?.isAMS) {
        return true;
      }
      
      return false;
    });
  }, [activities]);


  const generateDateActivityMap = useCallback(
    (activitiesResponse: Activity[], dateObject: DateTableObject) => {
      activitiesResponse.forEach((item) => {
        const dateParsed = parseISO(item.date);
        const date = format(dateParsed, 'yyyy-MM-dd');

        if (dateObject[date]) {
          dateObject[date] = [...dateObject[date], item];
        } else {
          dateObject[date] = [item];
        }
      });

      return dateObject;
    },
    []
  );

  const generateDefaultDatesObject = useCallback((filteredDates: Date[]) => {
    const dateObject: { [key: string]: Activity[] } = {};
    filteredDates.forEach((date) => {
      const dateString = format(date, 'yyyy-MM-dd');
      dateObject[dateString] = [];
    });

    return dateObject;
  }, []);

  const formattedActivities = useMemo(() => {
    const filteredDates = generateIntervalDates(startDate, endDate);
    const dateObject = generateDefaultDatesObject(filteredDates);
    const activitiesMap = generateDateActivityMap(activities, dateObject);

    const entries = Object.entries(activitiesMap);

    entries.sort(([dateA], [dateB]) => {
      const dateObjectA = new Date(formatDateOnly(dateA));
      const dateObjectB = new Date(formatDateOnly(dateB));

      return dateObjectB.getTime() - dateObjectA.getTime();
    });

    return entries;
  }, [
    activities,
    generateDateActivityMap,
    startDate,
    endDate,
    generateDefaultDatesObject,
  ]);

  const handleEditActivity = useCallback(
    (activity: Activity) => {
      if (onEditActivity) {
        onEditActivity(activity);
      }
    },
    [onEditActivity]
  );

  const handleDeleteActivity = useCallback(
    (activity: Activity) => {
      if (onDeleteActivity) {
        onDeleteActivity(activity);
      }
    },
    [onDeleteActivity]
  );

  const handleAddActivity = useCallback(
    (addDate) => {
      if (onAddActivity) {
        onAddActivity(addDate);
      }
    },
    [onAddActivity]
  );

  const { data: daysOffData } = useGetDaysOffQuery({
    filterDate: new Date(startDate).getFullYear(),
    country: location || '',
  });

  const daysOffMap = useMemo(() => {
    return (
      daysOffData?.map((dayOff) => {
        const parsedDate = parseISO(dayOff.dayOffDate);

        return format(parsedDate, 'yyyy-MM-dd');
      }) || []
    );
  }, [daysOffData, formattedActivities]);

  const isDayBeforeDayOff = useCallback(
    (checkDate: string) => {
      const checkDateObj = parse(checkDate, 'yyyy-MM-dd', new Date());
      const nextDay = addDays(checkDateObj, 1);

      const isDayBeforeHoliday = daysOffMap.some((date) => {
        const arrayDate = parse(date, 'yyyy-MM-dd', new Date());

        return isEqual(arrayDate, nextDay);
      });

      return isDayBeforeHoliday && location === COUNTRIES.MOLDOVA;
    },
    [daysOffMap, location]
  );

  const isDayOff = useCallback(
    (date) => daysOffMap.includes(date),
    [daysOffMap]
  );

  return (
    <TableContainer component={Paper}>
      <Table>
        <TableHead sx={styles.tableHead}>
          <TableRow>
            {isEmployeeSection && !isMobile && (
              <TableCell
                align="center"
                sx={[styles.columnName, styles.smallColumn]}
              >
                Actions
              </TableCell>
            )}
            {!isMobile && (
              <TableCell
                align="left"
                sx={[styles.columnName, styles.smallColumn]}
              >
                Date
              </TableCell>
            )}
            <TableCell
              align="left"
              sx={[styles.columnName, styles.mediumColumn]}
            >
              {isEmployeeSection ? 'Project' : 'Employee'}
            </TableCell>
            <TableCell
              align="left"
              sx={[styles.columnName, styles.mediumColumn]}
            >
              Task
            </TableCell>
            {!isMobile && hasAMSActivities && (
              <TableCell
                align="left"
                sx={[styles.columnName, styles.smallColumn]}
              >
                Ticket #
              </TableCell>
            )}
            <TableCell
              align="left"
              sx={[styles.columnName, styles.smallColumn]}
            >
              Logged(h)
            </TableCell>
            {!isMobile && (
              <TableCell
                align="left"
                sx={[styles.columnName, styles.mediumColumn]}
              >
                Comment
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {formattedActivities &&
            formattedActivities.map(([date, activityList]) => (
              <AppActivityTableRow
                key={date}
                activityDate={date}
                isDayOff={isDayOff(date)}
                isDayBeforeDayOff={isDayBeforeDayOff(date)}
                activityList={activityList}
                employeeWorkHours={employeeWorkHours}
                handleEditActivity={handleEditActivity}
                handleDeleteActivity={handleDeleteActivity}
                handleAddActivity={handleAddActivity}
                isEmployeeSection={isEmployeeSection}
                hasAMSActivities={hasAMSActivities}
              />
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
